import { Component, inject } from '@angular/core';
import { Subscription, fromEvent, map, merge, of } from 'rxjs';
import { RouterOutlet } from '@angular/router';
import { LoadingBarModule } from '@ngx-loading-bar/core';
import { HlmToaster } from '@spartan-ng/helm/sonner';
import { CommonModule } from '@angular/common';

import { AuthService } from '@src/app/core/services/auth.service';
import { NotificationService } from './shared/services/spartan-notification.service';
import { AppLoadingService } from './core/services/app-loading.service';
import { AppSkeletonWithProgressComponent } from './shared/components/skeleton/skeleton-with-progress.component';

import { RoutingHistoryService } from './core/services/routing-history.service';
import { BreakpointObserver } from '@angular/cdk/layout';
import { AppRepository } from './app.repository';
import { environment } from '@src/environments/environment';

/**
 * The root component of the application.
 */
@Component({
  selector: 'app-root',
  styleUrls: ['./app.component.less'],
  templateUrl: './app.component.html',
  standalone: true,
  imports: [RouterOutlet, LoadingBarModule, HlmToaster, CommonModule, AppSkeletonWithProgressComponent],
})
export class AppComponent {
  /**
   * Subscription for network status changes.
   */
  networkStatus$: Subscription = Subscription.EMPTY;

  /**
   * The application repository.
   */
  private readonly appRepository = inject(AppRepository);
  private notificationService = inject(NotificationService);
  protected readonly loadingService = inject(AppLoadingService);
  public readonly isProduction = environment.production;

  constructor(
    private _auth: AuthService,
    private routingHistory: RoutingHistoryService,
    private _breakpointObserver: BreakpointObserver,
  ) {}

  /**
   * Lifecycle hook that is called after the component is initialized.
   * It checks the network status.
   */
  ngOnInit(): void {
    this.checkNetworkStatus();
    this.initializeApp();
  }

  private async initializeApp(): Promise<void> {
    // Simulate app initialization
    await this.loadingService.initializeApp();
  }

  ngAfterViewInit(): void {
    this._breakpointObserver.observe(['(max-width: 1024px)']).subscribe((state) => {
      this.appRepository.setIsMobile(state.matches);
    });
  }

  /**
   * Checks the network status and updates the application store banner accordingly.
   */
  checkNetworkStatus() {
    this.networkStatus$ = merge(of(null), fromEvent(window, 'online'), fromEvent(window, 'offline'))
      .pipe(map(() => navigator.onLine))
      .subscribe((status) => {
        if (status) {
          this.appRepository.clearBanner();
        } else {
          this.appRepository.setBanner({ message: 'No internet connection' });
        }
      });
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * It unsubscribes from the network status subscription.
   */
  ngOnDestroy(): void {
    this.networkStatus$.unsubscribe();
  }
}
